import type { FunctionComponent } from 'react';
import React, { useCallback } from 'react';
import styles from './BoardShareButton.less';
import classNames from 'classnames';
import { forTemplate } from '@trello/i18n';
import { useCurrentBoardBackground } from 'app/src/components/BoardTheme';
import { AddMemberIcon } from '@trello/nachos/icons/add-member';
import { BoardHeaderTestIds } from '@trello/test-ids';
import { Analytics, formatContainers } from '@trello/atlassian-analytics';
import { Dialog } from 'app/scripts/views/lib/Dialog';
import {
  LazyBoardInviteModal,
  dialogProps,
} from 'app/src/components/BoardInviteModal';
import { ComponentWrapper } from 'app/src/components/ComponentWrapper';

const format = forTemplate('board_share_button');

interface BoardShareButtonProps {
  idBoard: string;
  idOrg: string;
}

export const BoardShareButton: FunctionComponent<BoardShareButtonProps> = ({
  idBoard,
  idOrg,
}) => {
  const { backgroundBrightness } = useCurrentBoardBackground();
  const isLightTheme = backgroundBrightness === 'light';

  const openBoardShareModal = useCallback(() => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'shareButton',
      source: 'boardScreen',
      containers: formatContainers({
        idBoard,
        idOrganization: idOrg,
        idWorkspace: idOrg,
      }),
    });

    Dialog.show({
      reactElement: (
        <ComponentWrapper>
          <LazyBoardInviteModal
            idBoard={idBoard}
            idOrg={idOrg}
            onClose={Dialog.hide}
          />
        </ComponentWrapper>
      ),
      ...dialogProps,
    });
  }, [idBoard, idOrg]);

  return (
    <button
      title={format('share-board')}
      className={classNames(
        styles.boardShareButton,
        isLightTheme && styles.lightBackground,
      )}
      data-testid={BoardHeaderTestIds.BoardShareButton}
      onClick={openBoardShareModal}
    >
      <AddMemberIcon
        color="currentColor"
        dangerous_className={styles.boardShareButtonIcon} // needed for print color styles
        size="small"
      />
      <span className={styles.boardShareButtonText}>{format('share')}</span>
    </button>
  );
};
